import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControlLabel,
  Radio,
} from "@mui/material";
import MainCalculatorsList from "../MainCalculators";
import RelatedLinks from "../RelatedLinks";
import { SecondAds,   FirstAds,MobileUnderCalAds } from "../../Ads";
import { CalculatorsType } from "../ConstantsEN";
import SharedHelmet from "../Helmet";
const TITLE = "Protein Calculator: Calculate Your Daily Protein Needs";
const DESCRIPTION = "Use the protein calculator to estimate the specified amount of protein to consume daily (grams/day) for weight loss and muscle gain.";
const KEYWORDS = "Protein calculator, Protein intake calculator, Daily protein needs, Protein for weight loss, Muscle building, Muscle strength improvement";
const OG_URL = "https://do-calculate.com/calculator/en/protein/";
const data1 = [
  { ageGroup: "1 -> 3", proteinNeeded: 13 },
  { ageGroup: "4 -> 8", proteinNeeded: 19 },
  { ageGroup: "9 -> 13", proteinNeeded: 34 },
  { ageGroup: "14 -> 18 (Girls)", proteinNeeded: 46 },
  { ageGroup: "14 -> 18 (Boys)", proteinNeeded: 52 },
  { ageGroup: "19 -> 70+ (Women)", proteinNeeded: 46 },
  { ageGroup: "19 -> 70+ (Men)", proteinNeeded: 56 },
];
const data2 = [
  {
    category: "First Trimester of Pregnancy",
    safeIntake: 1,
    additionalEnergyRequirement: "375 kJ/day",
    proteinEnergyRatio: 0.04,
  },
  {
    category: "Second Trimester of Pregnancy",
    safeIntake: 10,
    additionalEnergyRequirement: "1,200 kJ/day",
    proteinEnergyRatio: 0.11,
  },
  {
    category: "Third Trimester of Pregnancy",
    safeIntake: 31,
    additionalEnergyRequirement: "1,950 kJ/day",
    proteinEnergyRatio: 0.23,
  },
  {
    category: "First 6 Months of Breastfeeding",
    safeIntake: 19,
    additionalEnergyRequirement: "2,800 kJ/day",
    proteinEnergyRatio: 0.11,
  },
  {
    category: "Breastfeeding After First 6 Months",
    safeIntake: 13,
    additionalEnergyRequirement: "1,925 kJ/day",
    proteinEnergyRatio: 0.11,
  },
];
const data3 = [
  { food: "Milk (1 cup/8 oz)", proteinAmount: "8 g" },
  { food: "Egg (1 large/50 g)", proteinAmount: "6 g" },
  { food: "Meat (1 slice / 2 oz)", proteinAmount: "14 g" },
  { food: "Seafood (2 oz)", proteinAmount: "16 g" },
  { food: "Bread (1 slice/64 g)", proteinAmount: "8 g" },
  { food: "Corn (1 cup/166 g)", proteinAmount: "16 g" },
  { food: "Rice (1 cup/195 g)", proteinAmount: "5 g" },
  { food: "Dry Beans (1 cup/92 g)", proteinAmount: "16 g" },
  { food: "Nuts (1 cup/92 g)", proteinAmount: "20 g" },
  { food: "Fruits and Vegetables (1 cup)", proteinAmount: "0-1 g" },
  { food: "Pizza (1 slice/107 g)", proteinAmount: "12 g" },
];
function ProteinCalculator() {
  const [age, setAge] = useState("");
  const [gender, setGender] = useState("male");
  const [height, setHeight] = useState("");
  const [weight, setWeight] = useState("");
  const [activity, setActivity] = useState("sedentary");
  const [resultADA, setResultADA] = useState(null);
  const [resultCDC, setResultCDC] = useState(null);
  const [resultWHO, setResultWHO] = useState(null);
  const [ageError, setAgeError] = useState("");
  const [heightError, setHeightError] = useState("");
  const [weightError, setWeightError] = useState("");
  const handleAgeChange = (event) => {
    const inputAge = event.target.value;
    if (inputAge !== "") {
      const parsedAge = parseInt(inputAge, 10);
      if (!isNaN(parsedAge) && parsedAge >= 18 && parsedAge <= 85) {
        setAge(inputAge);
        setAgeError("");
      } else {
        setAge("");
        if (isNaN(parsedAge)) {
          setAgeError("Make sure to enter a number");
        } else if (parsedAge < 18) {
          setAge(inputAge);
          setAgeError("Age between 18 - 85");
        } else if (parsedAge > 85) {
          setAge(inputAge);
          setAgeError("Age between 18 - 85");
        }
      }
    } else {
      setAge("");
      setAgeError("");
    }
  };
  const handleHeightChange = (event) => {
    const inputHeight = event.target.value;
    if (inputHeight !== "") {
      if (!isNaN(inputHeight)) {
        setHeight(inputHeight);
        setHeightError("");
      } else {
        setHeight("");
        if (isNaN(inputHeight)) {
          setHeightError("Make sure to enter a number");
        }
      }
    } else {
      setHeight("");
      setHeightError("");
    }
  };
  const handleWeightChange = (event) => {
    const inputWeight = event.target.value;
    if (inputWeight !== "") {
      if (!isNaN(inputWeight)) {
        setWeight(inputWeight);
        setWeightError("");
      } else {
        setWeight("");
        if (isNaN(inputWeight)) {
          setWeightError("Make sure to enter a number");
        }
      }
    } else {
      setWeight("");
      setWeightError("");
    }
  };
  const calculateProtein = () => {
    const proteinIntakeADA = calculateProteinIntakeADA(
      age,
      gender,
      weight,
      activity,
      height
    );
    const proteinIntakeCDC = calculateProteinIntakeCDC(
      age,
      gender,
      weight,
      activity,
      height
    );
    const proteinIntakeWHO = calculateProteinIntakeWHO(
      age,
      gender,
      weight,
      activity,
      height
    );
    return {
      proteinIntakeADA: proteinIntakeADA,
      proteinIntakeCDC: proteinIntakeCDC,
      proteinIntakeWHO: proteinIntakeWHO,
    };
  };
  const calculateProteinIntakeADA = (age, gender, weight, activity) => {
    let proteinIntakeMin = 0;
    let proteinIntakeMax = 0;
    if (age >= 18 && age <= 80) {
      if (gender === "male" || gender === "female") {
        if (activity === "sedentary") {
          proteinIntakeMin = weight * 0.8;
          proteinIntakeMax = weight * 1.0;
        } else {
          proteinIntakeMin = weight * 1.0;
          proteinIntakeMax = weight * 1.8;
        }
      }
    }
    return `${Math.round(proteinIntakeMin)} - ${Math.round(proteinIntakeMax)}`;
  };
  const calculateProteinIntakeCDC = (age, gender, weight, activity, height) => {
    let bmr = 0;
    if (gender === "male") {
      bmr = 10 * weight + 6.25 * height - 5 * age + 5;
    } else if (gender === "female") {
      bmr = 10 * weight + 6.25 * height - 5 * age - 161;
    }
    let activityFactor = 1.0;
    switch (activity) {
      case "sedentary":
        activityFactor = 1.15;
        break;
      case "light":
        activityFactor = 1.325;
        break;
      case "moderate":
        activityFactor = 1.42;
        break;
      case "active":
        activityFactor = 1.52;
        break;
      case "veryActive":
        activityFactor = 1.67;
        break;
      case "extraActive":
        activityFactor = 1.85;
        break;
      default:
        activityFactor = 1.0;
        break;
    }
    const proteinIntakeMin = (0.1 * bmr * activityFactor) / 4;
    const proteinIntakeMax = (0.35 * bmr * activityFactor) / 4;
    return `${Math.round(proteinIntakeMin)} - ${Math.round(proteinIntakeMax)}`;
  };
  const calculateProteinIntakeWHO = (age, gender, weight, activity, height) => {
    let proteinIntake = 0;
    if (age >= 18 && age <= 80) {
      if (gender === "male" || gender === "female") {
        proteinIntake = weight * 0.83;
      }
    }
    return Math.round(proteinIntake);
  };
  const handleSubmit = (e) => {
    e.preventDefault();
    const result = calculateProtein();
    setResultADA(result.proteinIntakeADA);
    setResultCDC(result.proteinIntakeCDC);
    setResultWHO(result.proteinIntakeWHO);
  };
  return (
    <div className="ltr">
        <SharedHelmet
             TITLE={TITLE}
             DESCRIPTION={DESCRIPTION}
             KEYWORDS={KEYWORDS}
             OG_URL={OG_URL}
           />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "32px" }}
          >
            <h1 style={{ fontSize: "14px" }}>{TITLE}</h1>
          </div>
              <div className="border border-light">
              <div className="row">
  <div>
    <p>
      The protein calculator estimates the daily amount of dietary protein
      adults need to stay healthy. Typically, children, highly active
      individuals, pregnant and breastfeeding women require more protein.
      The calculator is also useful for monitoring protein intake for those
      with kidney diseases, liver diseases, diabetes, or other conditions
      where protein intake is a factor.
    </p>
    <hr />
  </div>
  <br />
</div>
<MobileUnderCalAds />
<div className="row">
  <div className="col-12 col-md-5 calculatorStyle">
    <div className="p-3 mb-2 bg-success text-white">
      Body Protein Calculator
    </div>
    <hr />
    <br />
    <form onSubmit={handleSubmit}>
      <div className="mb-3 d-flex" style={{ alignItems: "center" }}>
        <TextField
          label="Height"
          name="height"
          value={height}
          onChange={handleHeightChange}
          error={!!heightError}
          helperText={heightError}
          inputProps={{
            required: true,
            placeholder: "140",
          }}
          sx={{
            width: "130px",
            ml: "10px",
            bgcolor: "#f9f9f9",
            "&  .MuiFormHelperText-root.Mui-error": {
              backgroundColor: "rgb(229, 230, 227) !important",
              color: "red",
              margin: 0,
              padding: "8px 0 0 41px",
            },
          }}
          size="small"
          color="success"
        />
        <div
          style={{
            borderRadius: "4px",
            border: " 1px solid rgba(0, 0, 0, 0.23)",
            backgroundColor: "#f9f9f9",
            padding: "7.5px 12px",
            userSelect: "none",
            fontSize: "14px",
          }}
        >
          cm
        </div>
      </div>
            <div className="form-group d-flex align-items-center">
        <b>
          <label >Gender</label>
        </b>
        <div style={{ marginRight: "40px" }}>
          <FormControlLabel
            value="male"
            control={
              <Radio
                color="success"
                checked={gender === "male"}
                onChange={(e) => setGender(e.target.value)}
              />
            }
            label="Male"
            labelPlacement="start"
          />
          <FormControlLabel
            value="female"
            control={
              <Radio
                color="success"
                checked={gender === "female"}
                onChange={(e) => setGender(e.target.value)}
              />
            }
            label="Female"
            labelPlacement="start"
          />
        </div>
      </div>
            <br />
      <div>
        <b>
          <label className="form-label ms-2">Activity Rate</label>
        </b>
        <br />
        <Select
          value={activity}
          onChange={(e) => setActivity(e.target.value)}
          size="small"
          color="success"
          style={{
            width: "180px",
            overflow: "hidden",
            backgroundColor: "#f9f9f9",
            fontSize: "14px",
            padding: "2px 0",
          }}
        >
                  </Select>
      </div>
      <br />
      <hr />
      <div className="mb-3">
        <Button
          type="submit"
          color="success"
          sx={{
            backgroundColor: "rgb(25,135,84)",
            fontSize: "15px",
            marginRight: "25px",
            padding: "5px 25px",
            color: "white",
          }}
        >
          Calculate
        </Button>
              </div>
    </form>
  </div>
  <div className="col-12 col-md-7">
    {resultADA &&
    resultCDC &&
    resultWHO &&
    resultADA !== "0" &&
    resultCDC !== "0" &&
    resultWHO !== "0" ? (
      <div>
        <p style={{ fontWeight: "bold" }}>
          Based on the provided information, here are the basic protein
          intake recommendations from various reliable institutions:
        </p>
        <p>
          American Dietetic Association (ADA):{" "}
          <span
            style={{
              color: "rgb(25,135,84)",
              fontWeight: "bold",
            }}
          >
            {resultADA} grams per day
          </span>
        </p>
              </div>
    ) : (
      ""
    )}
  </div>
</div>
                <hr />
                <div className="row">
                  <RelatedLinks />
                  <hr />
                </div>
              </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList
            CalculatorsTypeId={CalculatorsType.healthCalculators}
          ></MainCalculatorsList>
          <br />
        </div>
      </div>
      <div className="row" style={{ height: "auto" }}>
        <div className="col-12 col-md-8">
          <div className="info-sections-container">
          <div className="info-section">
  <div className="info-section-header">
    Everything You Need to Know About Protein: How to Calculate It and Its Importance in Your Diet
  </div>
  <div className="info-section-body">
    <u>
      <h2 style={{ fontSize: "16px" }}>What is Protein?</h2>
    </u>
    <br />
    <p>
      Proteins are one of the three essential nutrients that provide energy to the human body, along with fats and carbohydrates. Proteins are also responsible for a significant portion of the cellular processes, being essential for the proper structure and function of tissues and organs, and they also contribute to their regulation. Proteins consist of a number of amino acids that are essential for the proper functioning of the body, serving as the fundamental building blocks for body tissues. There are 20 different amino acids in total, and the sequence of amino acids determines the structure and function of the protein. While some amino acids can be synthesized in the body, there are 9 essential amino acids that the human body cannot obtain except from food sources (insufficient amounts of which can sometimes lead to death), and these are called essential amino acids. Foods that provide all essential amino acids are called complete protein sources, including both animal sources (meat, dairy products, eggs, and fish) and plant sources (soybeans, quinoa, and black beans).
    </p>
    <hr />
    <u>
      <h2 style={{ fontSize: "16px" }}>Proteins can be classified based on the function they provide to the body. Here is a list of some types of proteins:</h2>{" "}
    </u>{" "}
    <br />
    <p>
      Antibodies: Proteins that protect the body from foreign particles, such as viruses and bacteria, by binding to them. <br />
      Enzymes: Proteins that aid in the formation of new molecules and perform various chemical reactions that occur throughout the body. <br />
      Messenger Proteins: Proteins that transmit signals throughout the body to maintain body processes. <br />
      Structural Component: Proteins that act as building blocks for cells, allowing the body to move, transport, and store.
    </p>
    <hr />
    <u>
      <h2 style={{ fontSize: "16px" }}>The Amount of Protein Needed by an Individual:</h2>
    </u>
    <br />
    <p>
      The amount of protein needed by the human body daily depends on various factors, including total energy consumption, individual growth, and physical activity level. It is often estimated based on body weight as a percentage of total calories (10-35%), or based on age alone. 0.8 grams per kilogram of body weight is a commonly recommended dietary limit. This value is the minimum recommended to maintain basic nutritional requirements, but consuming more protein, up to a certain point, may be beneficial depending on protein sources. The recommended protein intake ranges between 0.8 grams/kg and 1.8 grams/kg of body weight, depending on the aforementioned factors. Highly active individuals or those looking to build more muscle generally need to consume more protein. Some sources suggest a consumption range of 1.8 to 2 grams/kg for highly active individuals. The precise amount of protein a person should consume is not yet an exact science, and each individual should consult a specialist, whether a nutritionist, doctor, or personal trainer, to help determine their individual needs.
    </p>
    <hr />
    <u>
      <h2 style={{ fontSize: "16px" }}>Foods with High Protein Content:</h2>
    </u>
    <br />
    <p>
      There are various food groups that anyone can consume to meet their protein requirements. For many people, a significant portion of protein comes from consuming meat and dairy products, although it is possible to obtain sufficient protein while adhering to certain dietary restrictions. Generally, it is easier to meet the recommended amount of protein by consuming meat and dairy products, but excessive intake of either can have a negative impact on health. There are plenty of plant-based protein options, although they generally contain a lower amount of protein in a specific meal. Ideally, a person should consume a mix of meat, dairy products, and plant-based foods to meet their daily nutritional needs and maintain a balanced diet full of nutrients. <br />
      Examples of Meat/Dairy: Eggs, chicken breast, cheese, milk, beef, tuna, turkey breast, shrimp. <br />
      Plant-based Examples: Black beans, chickpeas, soy products, peanut butter on toast or any other bread, hemp seeds, rice, avocado, mushrooms. <br />
      In general, meats, poultry, fish, eggs, and dairy products are considered complete protein sources. Nuts, seeds, legumes, grains, and vegetables, among other things, usually constitute incomplete proteins. However, there's no harm in incomplete proteins, and there are many healthy protein-rich foods considered incomplete proteins. As long as you consume a sufficient variety of incomplete proteins to obtain all the required amino acids, it's not necessary to specifically consume complete protein foods. In fact, some red meats rich in fat, for example, a common source of complete proteins, can be unhealthy. <br />
      Here are some examples of protein-rich foods that are not complete proteins: Almonds, oats, broccoli, lentils, bread, pumpkin seeds, peanuts, green peas, avocado, mushrooms.
    </p>
    <hr />
  </div>
</div>
          </div>
          <br />
          <u>
  <h2>Some Illustrative Tables:</h2>
</u>
<br />
<div className="info-sections-container">
  <div className="info-section">
    <div className="info-section-header">
      <h3 style={{ fontSize: "16px" }}>
        Recommended Dietary Protein Substitute, Based on Age
      </h3>
    </div>
    <div className="info-section-body">
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: "#4caf50" }}>
              <TableCell style={{ color: "white" }}>
                Required Protein (grams/day)
              </TableCell>
              <TableCell style={{ color: "white" }}>
                Age Group (Years)
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data1.map((row, index) => (
              <TableRow
                key={row.ageGroup}
                style={{
                  backgroundColor: index % 2 === 0 ? "white" : "#eeeeee",
                }}
              >
                <TableCell>{row.proteinNeeded}</TableCell>
                <TableCell>{row.ageGroup}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  </div>
  <div className="info-section">
    <div className="info-section-header">
      <h3 style={{ fontSize: "16px" }}>
        Additional Protein Requirements for Pregnancy and Breastfeeding:
      </h3>
    </div>
    <div className="info-section-body">
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: "#4caf50" }}>
              <TableCell style={{ color: "white" }}>Category</TableCell>
              <TableCell style={{ color: "white" }}>
                Safe Intake (grams/day)
              </TableCell>
              <TableCell style={{ color: "white" }}>
                Additional Energy Requirement (kJ/day)
              </TableCell>
              <TableCell style={{ color: "white" }}>
                Protein: Energy Ratio
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data2.map((row, index) => (
              <TableRow
                key={row.category}
                style={{
                  backgroundColor: index % 2 === 0 ? "white" : "#eeeeee",
                }}
              >
                <TableCell>{row.category}</TableCell>
                <TableCell>{row.safeIntake}</TableCell>
                <TableCell>{row.additionalEnergyRequirement}</TableCell>
                <TableCell>{row.proteinEnergyRatio}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  </div>
  <div className="info-section">
    <div className="info-section-header">
      <h3 style={{ fontSize: "16px" }}>
        Protein Content in Common Foods:
      </h3>
    </div>
    <div className="info-section-body">
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow style={{ backgroundColor: "#4caf50" }}>
              <TableCell style={{ color: "white" }}>Food</TableCell>
              <TableCell style={{ color: "white" }}>Protein Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data3.map((row, index) => (
              <TableRow
                key={row.food}
                style={{
                  backgroundColor: index % 2 === 0 ? "white" : "#eeeeee",
                }}
              >
                <TableCell>{row.food}</TableCell>
                <TableCell>{row.proteinAmount}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  </div>
</div>
        </div>
         </div>
</div>
  );
}
export default ProteinCalculator;
